import React, { useState, useEffect } from "react";
import { useHistory, useParams } from "react-router-dom";
import {
    makeStyles,
    Typography,
    Paper,
    Grid,
    Box,
    Button,
    TextField,
    FormControl,
    InputLabel,
    Select,
    MenuItem,
    Input,
} from "@material-ui/core";
import { ToggleButton, ToggleButtonGroup } from "@material-ui/lab";
import ArrowBackIcon from "@material-ui/icons/ArrowBack";
import SaveIcon from "@material-ui/icons/Save";
import CloudUploadIcon from "@material-ui/icons/CloudUpload";
import apiHelper from "../../../util/ApiHelper";
import { useLoading } from "../../../util/LoadingProvider";
import ImageUploadField from "../../common/ImageUploadField";

const useStyles = makeStyles((theme) => ({
    content: {
        padding: theme.spacing(3),
    },
    header: {
        marginBottom: theme.spacing(3),
        display: "flex",
        alignItems: "center",
        gap: theme.spacing(2),
    },
    backButton: {
        marginRight: theme.spacing(2),
    },
    formCard: {
        padding: theme.spacing(3),
        marginBottom: theme.spacing(3),
    },
    formField: {
        marginBottom: theme.spacing(2),
    },
    buttonGroup: {
        display: "flex",
        gap: theme.spacing(2),
        marginTop: theme.spacing(3),
    },
    fileInput: {
        width: "100%",
        marginBottom: "12px",
        "& .MuiInputLabel-outlined": {
            transform: "translate(14px, 13px)",
        },
        "& .MuiOutlinedInput-adornedEnd": {
            height: "40px",
            paddingRight: "5px",
        },
        "& input": {
            fontSize: "13px",
        },
    },
    sectionText: {
        fontSize: "13px",
        fontWeight: 700,
        paddingLeft: "4px",
    },
    statusToggle: {
        width: "50%",
        "& button": {
            fontSize: "13px",
            lineHeight: "15px",
        },
    },
    statusField: {
        marginTop: theme.spacing(3),
    },
}));

const VoucherEditingPage = () => {
    const classes = useStyles();
    const history = useHistory();
    const { id } = useParams();
    const { setLoading } = useLoading();
    
    const isEditMode = !!id;
    
    const [formData, setFormData] = useState({
        category: "",
        expiryDate: "",
        pointPrice: "",
        orderLimit: "",
        titleEn: "",
        titleTc: "",
        imagePath: "",
        status: true,
    });

    const [errors, setErrors] = useState({});
    const [isUploading, setIsUploading] = useState(false);

    // Load existing voucher data if in edit mode
    useEffect(() => {
        if (isEditMode) {
            loadVoucherData();
        }
    }, [id]);

    const loadVoucherData = async () => {
        setLoading(true);
        try {
            const response = await apiHelper.cineclub.getVoucherById(id);
            if (response.data) {
                const voucher = response.data;
                setFormData({
                    category: voucher.category || "",
                    expiryDate: voucher.expiryDate || "",
                    pointPrice: voucher.pointPrice || "",
                    orderLimit: voucher.orderLimit != null ? String(voucher.orderLimit) : "",
                    titleEn: voucher.titleEn || "",
                    titleTc: voucher.titleTc || "",
                    imagePath: voucher.imagePath || "",
                    status: voucher.status !== undefined ? voucher.status : true,
                });
            }
        } catch (error) {
            console.error("Error loading voucher data:", error);
            alert("Failed to load voucher data. Please try again.");
        } finally {
            setLoading(false);
        }
    };

    const handleInputChange = (field) => (event) => {
        const value = event.target.value;
        setFormData(prev => ({
            ...prev,
            [field]: value
        }));
        
        // Clear error when user starts typing
        if (errors[field]) {
            setErrors(prev => ({
                ...prev,
                [field]: ""
            }));
        }
    };

    const validateForm = () => {
        const newErrors = {};
        
        if (!formData.category) {
            newErrors.category = "Category is required";
        }
        
        if (!formData.expiryDate) {
            newErrors.expiryDate = "Expiry date is required";
        }
        
        if (!formData.pointPrice || isNaN(formData.pointPrice) || parseInt(formData.pointPrice) < 0) {
            newErrors.pointPrice = "Point price must be a non-negative number";
        }
        
        if (formData.orderLimit && (isNaN(formData.orderLimit) || parseInt(formData.orderLimit) < 0 || !Number.isInteger(parseFloat(formData.orderLimit)))) {
            newErrors.orderLimit = "Order limit must be an integer or empty";
        }

        if (!formData.titleEn.trim()) {
            newErrors.titleEn = "English title is required";
        }
        
        if (!formData.titleTc.trim()) {
            newErrors.titleTc = "Traditional Chinese title is required";
        }
        
        setErrors(newErrors);
        return Object.keys(newErrors).length === 0;
    };

    const handleSubmit = async () => {
        if (!validateForm()) {
            return;
        }

        setLoading(true);
        try {
            const payload = {
                category: formData.category,
                expiryDate: formData.expiryDate,
                pointPrice: parseInt(formData.pointPrice),
                titleEn: formData.titleEn.trim(),
                titleTc: formData.titleTc.trim(),
                imagePath: formData.imagePath,
                orderLimit: formData.orderLimit ? parseInt(formData.orderLimit) : null,
                status: formData.status,
            };

            let response;
            if (isEditMode) {
                response = await apiHelper.cineclub.updateVoucherTemplate(id, payload);
            } else {
                response = await apiHelper.cineclub.createVoucherTemplate(payload);
            }
            
            // Log the response to debug
            console.log("API Response:", response);
            
            if (response.data && response.data.code === 0) {
                // Success - redirect back to listing page
                history.push("/cineclub/eshop/vouchers");
            } else {
                // Handle error response
                const action = isEditMode ? "update" : "create";
                console.error(`Failed to ${action} voucher template:`, response);
                alert(`Failed to ${action} voucher template. Please try again.`);
            }
        } catch (error) {
            const action = isEditMode ? "updating" : "creating";
            console.error(`Error ${action} voucher template:`, error);
            alert(`An error occurred while ${action} the voucher template. Please try again.`);
        } finally {
            setLoading(false);
        }
    };

    const handleCancel = () => {
        history.push("/cineclub/eshop/vouchers");
    };

    const handleImageUpload = async (field, file) => {
        if (!file) return;

        try {
            setLoading(true);
            const result = await apiHelper.cineclub.uploadMembershipFile(file, "VOUCHER");
            const url = result.data.url;
            // Store the full URL for display
            setFormData(prev => ({
                ...prev,
                [field]: url
            }));
        } catch (error) {
            console.error("Error uploading file:", error);
            alert("Failed to upload image. Please try again.");
        } finally {
            setLoading(false);
        }
    };

    const handleImageDelete = (field) => {
        setFormData(prev => ({
            ...prev,
            [field]: ""
        }));
    };

    const handleFileSelect = (event) => {
        const file = event.target.files[0];
        if (file) {
            // Validate file type
            if (file.type !== 'application/zip' && !file.name.toLowerCase().endsWith('.zip')) {
                alert('Please select a ZIP file.');
                return;
            }
            // Automatically upload the file after selection
            uploadFile(file);
        }
    };

    const uploadFile = async (file) => {
        setIsUploading(true);
        try {
            const response = await apiHelper.cineclub.uploadVoucherImage(id, file);
            
            if (response.data && response.data.code === 0) {
                alert('Voucher images uploaded successfully!');
                // Reset file input
                const fileInput = document.getElementById('file-upload-input');
                if (fileInput) {
                    fileInput.value = '';
                }
                // Reload voucher data to reflect latest stock
                await loadVoucherData();
            } else {
                console.error("Failed to upload voucher images:", response);
                alert("Failed to upload voucher images. Please try again.");
            }
        } catch (error) {
            console.error("Error uploading voucher images:", error);
            alert("An error occurred while uploading the voucher images. Please try again.");
        } finally {
            setIsUploading(false);
        }
    };

    return (
        <div className={classes.content}>
            <Box className={classes.header}>
                <Button
                    className={classes.backButton}
                    startIcon={<ArrowBackIcon />}
                    onClick={handleCancel}
                >
                    Back to Vouchers
                </Button>
                <Typography variant="h4" component="h1">
                    {isEditMode ? "Edit Voucher Template" : "Create Voucher Template"}
                </Typography>
            </Box>

            <Paper className={classes.formCard}>
                <Typography variant="h6" gutterBottom>
                    Voucher Template Details
                </Typography>
                
                <Grid container spacing={3}>
                    <Grid item xs={12} md={6}>
                        <FormControl 
                            fullWidth 
                            variant="outlined" 
                            className={classes.formField}
                            error={!!errors.category}
                        >
                            <InputLabel>Category</InputLabel>
                            <Select
                                value={formData.category}
                                onChange={handleInputChange("category")}
                                label="Category"
                            >
                                <MenuItem value="A">Category A</MenuItem>
                                <MenuItem value="B">Category B</MenuItem>
                                <MenuItem value="C">Category C</MenuItem>
                            </Select>
                            {errors.category && (
                                <Typography variant="caption" color="error">
                                    {errors.category}
                                </Typography>
                            )}
                        </FormControl>
                    </Grid>

                    <Grid item xs={12} md={6}>
                        <TextField
                            fullWidth
                            label="Expiry Date"
                            type="date"
                            variant="outlined"
                            className={classes.formField}
                            value={formData.expiryDate}
                            onChange={handleInputChange("expiryDate")}
                            InputLabelProps={{
                                shrink: true,
                            }}
                            error={!!errors.expiryDate}
                            helperText={errors.expiryDate}
                        />
                    </Grid>

                    <Grid item xs={12} md={6}>
                        <TextField
                            fullWidth
                            label="English Title"
                            variant="outlined"
                            className={classes.formField}
                            value={formData.titleEn}
                            onChange={handleInputChange("titleEn")}
                            error={!!errors.titleEn}
                            helperText={errors.titleEn}
                        />
                    </Grid>

                    <Grid item xs={12} md={6}>                    
                        <TextField
                            fullWidth
                            label="Chinese Title"
                            variant="outlined"
                            className={classes.formField}
                            value={formData.titleTc}
                            onChange={handleInputChange("titleTc")}
                            error={!!errors.titleTc}
                            helperText={errors.titleTc}
                        />
                    </Grid>

                    <Grid item xs={12} md={6}>
                        <TextField
                            fullWidth
                            label="Point Price"
                            type="number"
                            variant="outlined"
                            className={classes.formField}
                            value={formData.pointPrice}
                            onChange={handleInputChange("pointPrice")}
                            error={!!errors.pointPrice}
                            helperText={errors.pointPrice}
                            inputProps={{ min: 0 }}
                        />
                    </Grid>

                    <Grid item xs={12} md={6}>
                        <TextField
                            fullWidth
                            label="Order Limit"
                            type="number"
                            variant="outlined"
                            className={classes.formField}
                            value={formData.orderLimit}
                            onChange={handleInputChange("orderLimit")}
                            error={!!errors.orderLimit}
                            helperText={errors.orderLimit || "Leave empty for no limit, or enter an integer"}
                            inputProps={{ min: 0, step: 1 }}
                        />
                    </Grid>

                    <Grid item xs={12}>
                        <ImageUploadField
                            field="imagePath"
                            value={formData.imagePath}
                            onUpload={handleImageUpload}
                            onDelete={handleImageDelete}
                            label="Image Thumbnail"
                            classes={classes}
                        />
                    </Grid>

                    {isEditMode && (
                        <Grid item xs={12}>
                            <Paper style={{ padding: '16px', marginTop: '16px', backgroundColor: '#f5f5f5' }}>
                                <Typography variant="h6" gutterBottom>
                                    Upload Voucher Images
                                </Typography>
                                <Typography variant="body2" color="textSecondary" gutterBottom>
                                    Upload a ZIP file containing voucher images. This feature is only available when editing existing vouchers.
                                    {" "}
                                    <a href="/sample/COUPON_sample.zip" download="COUPON_sample.zip">
                                        Download sample file
                                    </a>
                                </Typography>
                                <Box display="flex" alignItems="center" gap={2} marginTop={2}>
                                    <Button
                                        variant="contained"
                                        color="tertiary"
                                        component="label"
                                        startIcon={<CloudUploadIcon />}
                                        disabled={isUploading}
                                    >
                                        {isUploading ? 'Uploading...' : 'Upload ZIP File'}
                                        <input
                                            id="file-upload-input"
                                            type="file"
                                            accept=".zip"
                                            onChange={handleFileSelect}
                                            style={{ display: 'none' }}
                                        />
                                    </Button>
                                </Box>
                            </Paper>
                        </Grid>
                    )}
                    
                </Grid>

                <Grid item xs={12} className={classes.statusField}>
                    <Typography variant="body2" className={classes.sectionText}>
                        Status
                    </Typography>
                    <ToggleButtonGroup
                        value={formData.status ? "ACTIVE" : "INACTIVE"}
                        onChange={(event, newValue) => {
                            if (newValue !== null) {
                                setFormData(prev => ({
                                    ...prev,
                                    status: newValue === "ACTIVE"
                                }));
                            }
                        }}
                        exclusive
                        aria-label="Status"
                        className={classes.statusToggle}
                    >
                        <ToggleButton
                            value="ACTIVE"
                            aria-label="Active"
                            style={{ width: "50%" }}
                        >
                            ACTIVE
                        </ToggleButton>
                        <ToggleButton
                            value="INACTIVE"
                            aria-label="Inactive"
                            style={{ width: "50%" }}
                        >
                            INACTIVE
                        </ToggleButton>
                    </ToggleButtonGroup>
                </Grid>

                <Box className={classes.buttonGroup}>
                    <Button
                        variant="contained"
                        color="primary"
                        startIcon={<SaveIcon />}
                        onClick={handleSubmit}
                    >
                        {isEditMode ? "Update" : "Create"}
                    </Button>
                    <Button
                        variant="outlined"
                        onClick={handleCancel}
                    >
                        Cancel
                    </Button>
                </Box>
            </Paper>
        </div>
    );
};

export default VoucherEditingPage;
